/**
 * DIV-026 — size-matched variant resolution for the CT Non-Variant ATC path.
 *
 * In CT (Non-Variant) mode `HandleAddToCartEvent` always wrote
 * `appState.baseVariantId` (stamped once from `variants[0]` at page load)
 * into `#variantIdCustom`, regardless of what the shopper picked in the
 * calculator itself — because nothing on the page's native Shopify variant
 * picker ever changed (there usually isn't one; the calculator IS the size
 * picker). Cart Transform still prices the line correctly off the `cpc_`
 * token, but the wrong Shopify variant rides along, so any per-variant
 * property — weight, in particular — is wrong at checkout (issue #144,
 * Campaign Direct, e1nmhm-uc.myshopify.com, Install ID 12520: shipping
 * quoted off a stopgap 8.93g on every size instead of each variant's real
 * weight).
 *
 * `resolveVariantId` matches the same way Shopify's own variant picker does:
 * by option NAME + VALUE. A calculator element is treated as answering one
 * of the product's options when its `properties[...]` name equals that
 * option's name, case-insensitively (e.g. a "Size" element for a "Size"
 * option) — exactly the "bound on Size alone" shape merchants configure
 * today, no new admin UI/mapping required. Resolution is deliberately
 * all-or-nothing: every one of the product's options must have a matching,
 * non-empty calculator property AND the combination must identify exactly
 * one variant, or `null` is returned and the caller keeps writing
 * `baseVariantId` — today's exact behavior. That makes this strictly
 * additive: a store with no calculator element named after a Shopify option
 * (the common case) resolves nothing and is byte-identical to before.
 *
 * DIV-042 — two fixes on top (issue #212, same store):
 *  - `readOptionNames` reads option names from the shape Shopify's
 *    `/products/<handle>.js` actually serves (`{ name, position, values }`
 *    objects). DIV-026 kept plain strings only, so the list was always empty
 *    in production and this resolver never matched on any store.
 *  - A uniquely matched variant Shopify reports `available: false` (tracked
 *    stock at 0, "continue selling" off) is not used: the caller keeps
 *    `baseVariantId`, so a cart that worked before can't start failing with
 *    "sold out" because resolution now runs.
 */
import type { ProductVariantOptionSummary } from "../state/app-state";

/**
 * Option names from product.js `options`, positional with variant
 * option1/2/3. A plain string is taken as-is (the shape DIV-026 was written
 * against); otherwise an object's non-empty string `name` (the shape Shopify
 * serves). Anything else is skipped — the resolver then can't answer every
 * option and falls back to the base variant, as before.
 */
export function readOptionNames(options: unknown): string[] {
  var names: string[] = [];
  if (!Array.isArray(options)) return names;
  options.forEach(function (option: unknown) {
    if (typeof option === "string") {
      names.push(option);
      return;
    }
    if (option !== null && typeof option === "object") {
      var name = (option as { name?: unknown }).name;
      if (typeof name === "string" && name.trim() !== "") names.push(name);
    }
  });
  return names;
}

export function resolveVariantId(
  properties: Record<string, unknown>,
  optionNames: readonly string[],
  variants: readonly ProductVariantOptionSummary[],
): number | null {
  if (optionNames.length === 0 || variants.length <= 1) return null;

  var propertyKeysByLowerName: Record<string, string> = {};
  Object.keys(properties).forEach(function (key) {
    propertyKeysByLowerName[key.toLowerCase()] = key;
  });

  var selectedValues: string[] = [];
  for (var i = 0; i < optionNames.length; i++) {
    var propertyKey = propertyKeysByLowerName[optionNames[i]!.toLowerCase()];
    if (propertyKey === undefined) return null;
    var rawValue = properties[propertyKey];
    if (rawValue === undefined || rawValue === null) return null;
    var value = String(rawValue).trim();
    if (value === "") return null;
    selectedValues.push(value.toLowerCase());
  }

  var optionKeys = ["option1", "option2", "option3"] as const;
  var matches = variants.filter(function (variant) {
    for (var i = 0; i < selectedValues.length; i++) {
      var variantValue = variant[optionKeys[i]!];
      if (variantValue == null) return false;
      if (String(variantValue).trim().toLowerCase() !== selectedValues[i]) return false;
    }
    return true;
  });

  if (matches.length !== 1) return null;
  // DIV-042: never swap in a variant Shopify says can't be bought; a missing
  // flag counts as available.
  if (matches[0]!.available === false) return null;
  return matches[0]!.id;
}
